import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface SaleBarn {
  name: string;
  state: string;
  city: string;
  totalSales: number;
  totalHead: number;
  lastSaleDate: string | null;
}

/**
 * Dynamic registry of every sale barn with sales in the last 90 days.
 *
 * Replaces the hardcoded SALE_BARN_LOCATIONS list for components that
 * just need barn names + city/state (e.g., GlobalSearch). Components
 * that need geographic coordinates (FreightImpactCalculator, WhereToBuy)
 * still use the hardcoded list until we geocode this dynamic list.
 *
 * Source: v_sale_barns view, which auto-populates from
 * dv_sale_market_reports and filters out test data.
 */
export function useSaleBarns() {
  return useQuery<SaleBarn[]>({
    queryKey: ["sale-barns"],
    queryFn: async () => {
      const { data, error } = await (supabase as unknown as {
        from: (t: string) => {
          select: (cols: string) => Promise<{ data: any[] | null; error: Error | null }>;
        };
      })
        .from("v_sale_barns")
        .select("name, state, city, total_sales, total_head, last_sale_date");

      if (error) throw error;

      return (data ?? []).map((r: any) => ({
        name: r.name,
        state: r.state,
        city: r.city,
        totalSales: Number(r.total_sales ?? 0),
        totalHead: Number(r.total_head ?? 0),
        lastSaleDate: r.last_sale_date ?? null,
      }));
    },
    // Barn list rarely changes. 10-minute staleTime is plenty.
    staleTime: 10 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  });
}
